import React, { useState, useEffect } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
    ScrollView,
    RefreshControl
} from 'react-native';
import Alert from "@/lib/Alert";
import { useQueryClient } from '@tanstack/react-query';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {asyncStoragePersister} from "@/data/app-data-persister";
import {ThemeType} from "@/theming/baseTheme";

// Data Item Component
interface DataItemProps {
    title: string;
    data: any;
    onClear?: () => void;
    expandable?: boolean;
}

const DataItem: React.FC<DataItemProps> = ({ title, data, onClear, expandable = true }) => {
    const {theme} = useCurrentTheme();
    const styles = createDataItemStyles(theme);
    const [isExpanded, setIsExpanded] = useState(false);

    const formatData = (data: any): string => {
        if (data === null) return 'null';
        if (data === undefined) return 'undefined';
        if (typeof data === 'string') return data;

        try {
            return JSON.stringify(data, null, 2);
        } catch (error) {
            return String(data);
        }
    };

    const getDataSize = (data: any): string => {
        const jsonString = formatData(data);
        const sizeInBytes = new Blob([jsonString]).size;

        if (sizeInBytes < 1024) return `${sizeInBytes} B`;
        if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(1)} KB`;
        return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const dataPreview = formatData(data);
    const isLargeData = dataPreview.length > 200;

    return (
        <View style={styles.container}>
            <TouchableOpacity
                style={styles.header}
                onPress={() => expandable && setIsExpanded(!isExpanded)}
                disabled={!expandable}
            >
                <View style={styles.titleRow}>
                    <Text style={styles.title}>{title}</Text>
                    <Text style={styles.size}>{getDataSize(data)}</Text>
                </View>

                <View style={styles.metaRow}>
                    <Text style={styles.type}>
                        {data === null ? 'null' : typeof data}
                        {Array.isArray(data) ? ' (array)' : ''}
                    </Text>
                    {expandable && (
                        <Text style={styles.expandIcon}>
                            {isExpanded ? '▼' : '▶'}
                        </Text>
                    )}
                </View>
            </TouchableOpacity>

            {isExpanded && (
                <View style={styles.content}>
                    <ScrollView
                        style={styles.dataContainer}
                        nestedScrollEnabled
                        showsVerticalScrollIndicator={true}
                    >
                        <Text style={styles.dataText}>
                            {isLargeData && !isExpanded
                                ? `${dataPreview.substring(0, 200)}...`
                                : dataPreview
                            }
                        </Text>
                    </ScrollView>

                    {onClear && (
                        <TouchableOpacity
                            style={styles.clearButton}
                            onPress={onClear}
                        >
                            <Text style={styles.clearButtonText}>Clear</Text>
                        </TouchableOpacity>
                    )}
                </View>
            )}
        </View>
    );
};

// Stats Component
interface StatsProps {
    totalItems: number;
    totalSize: string;
    lastUpdated: string;
}

const Stats: React.FC<StatsProps> = ({ totalItems, totalSize, lastUpdated }) => {
    const {theme} = useCurrentTheme();
    const styles = createStatsStyles(theme);

    return (
        <View style={styles.container}>
            <Text style={styles.title}>Storage Overview</Text>
            <View style={styles.statsRow}>
                <View style={styles.statItem}>
                    <Text style={styles.statNumber}>{totalItems}</Text>
                    <Text style={styles.statLabel}>Items</Text>
                </View>
                <View style={styles.statItem}>
                    <Text style={styles.statNumber}>{totalSize}</Text>
                    <Text style={styles.statLabel}>Total Size</Text>
                </View>
                <View style={styles.statItem}>
                    <Text style={styles.statNumber}>{lastUpdated}</Text>
                    <Text style={styles.statLabel}>Last Updated</Text>
                </View>
            </View>
        </View>
    );
};

// Main Offline Data Screen Component
export default function OfflineDataScreen() {
    const {theme} = useCurrentTheme();
    const styles = createPageStyles(theme);
    const queryClient = useQueryClient();

    const [asyncStorageData, setAsyncStorageData] = useState<Record<string, any>>({});
    const [queryCache, setQueryCache] = useState<any>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

    const loadOfflineData = async () => {
        try {
            // Get all AsyncStorage keys
            const keys = await AsyncStorage.getAllKeys();
            const stores = await AsyncStorage.multiGet(keys);

            const storageData: Record<string, any> = {};
            stores.forEach(([key, value]) => {
                try {
                    // Try to parse as JSON, fallback to string
                    storageData[key] = value ? JSON.parse(value) : null;
                } catch {
                    storageData[key] = value;
                }
            });

            setAsyncStorageData(storageData);

            // Get TanStack Query cache
            const cache = queryClient.getQueryCache().getAll();
            setQueryCache(cache);

            setLastRefresh(new Date());
        } catch (error) {
            console.error('Failed to load offline data:', error);
            Alert.alert('Error', 'Failed to load offline data');
        }
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        await loadOfflineData();
        setIsRefreshing(false);
    };

    const clearAsyncStorageItem = async (key: string) => {
        Alert.alert(
            'Clear Data',
            `Are you sure you want to clear "${key}"?`,
            [
                { text: 'Cancel', style: 'cancel' },
                {
                    text: 'Clear',
                    style: 'destructive',
                    onPress: async () => {
                        try {
                            await AsyncStorage.removeItem(key);
                            await loadOfflineData();
                            Alert.alert('Success', 'Data cleared successfully');
                        } catch (error: any) {
                            Alert.alert('Error', 'Failed to clear data');
                        }
                    }
                }
            ]
        );
    };

    const clearAllData = () => {
        Alert.alert(
            'Clear All Data',
            'This will clear ALL offline data including cache and settings. Are you sure?',
            [
                { text: 'Cancel', style: 'cancel' },
                {
                    text: 'Clear All',
                    style: 'destructive',
                    onPress: async () => {
                        try {
                            // Clear AsyncStorage
                            await AsyncStorage.clear();

                            // Clear TanStack Query cache
                            queryClient.clear();

                            // Clear persisted cache
                            await asyncStoragePersister.removeClient();

                            await loadOfflineData();
                            Alert.alert('Success', 'All data cleared successfully');
                        } catch (error: any) {
                            Alert.alert('Error', 'Failed to clear all data');
                        }
                    }
                }
            ]
        );
    };

    useEffect(() => {
        loadOfflineData();
    }, []);

    const calculateTotalSize = () => {
        const allData = { ...asyncStorageData, queryCache };
        const jsonString = JSON.stringify(allData);
        const sizeInBytes = new Blob([jsonString]).size;

        if (sizeInBytes < 1024) return `${sizeInBytes} B`;
        if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(1)} KB`;
        return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const totalItems = Object.keys(asyncStorageData).length + (queryCache?.length || 0);

    return (
        <ScrollView
            style={styles.container}
            refreshControl={
                <RefreshControl
                    refreshing={isRefreshing}
                    onRefresh={handleRefresh}
                    tintColor={theme.colors.primary}
                />
            }
        >
            {/* Header */}
            <View style={styles.header}>
                <Text style={styles.title}>Offline Data</Text>
                <Text style={styles.subtitle}>
                    Inspect persisted data and cache
                </Text>
            </View>

            {/* Stats */}
            <Stats
                totalItems={totalItems}
                totalSize={calculateTotalSize()}
                lastUpdated={lastRefresh.toLocaleTimeString()}
            />

            {/* Actions */}
            <View style={styles.actions}>
                <TouchableOpacity style={styles.refreshButton} onPress={handleRefresh}>
                    <Text style={styles.refreshButtonText}>Refresh Data</Text>
                </TouchableOpacity>

                <TouchableOpacity style={styles.clearAllButton} onPress={clearAllData}>
                    <Text style={styles.clearAllButtonText}>Clear All Data</Text>
                </TouchableOpacity>
            </View>

            {/* AsyncStorage Data */}
            <View style={styles.section}>
                <Text style={styles.sectionTitle}>AsyncStorage Data</Text>
                {Object.keys(asyncStorageData).length === 0 ? (
                    <Text style={styles.emptyText}>No AsyncStorage data found</Text>
                ) : (
                    Object.entries(asyncStorageData).map(([key, value]) => (
                        <DataItem
                            key={key}
                            title={key}
                            data={value}
                            onClear={() => clearAsyncStorageItem(key)}
                        />
                    ))
                )}
            </View>

            {/* Query Cache */}
            <View style={styles.section}>
                <Text style={styles.sectionTitle}>TanStack Query Cache</Text>
                {!queryCache || queryCache.length === 0 ? (
                    <Text style={styles.emptyText}>No query cache found</Text>
                ) : (
                    queryCache.map((query: any, index: number) => (
                        <DataItem
                            key={index}
                            title={`Query: ${JSON.stringify(query.queryKey)}`}
                            data={{
                                state: query.state,
                                queryKey: query.queryKey,
                                queryHash: query.queryHash,
                                options: query.options
                            }}
                            expandable={true}
                        />
                    ))
                )}
            </View>

            {/* Bottom spacing */}
            <View style={styles.bottomSpacing} />
        </ScrollView>
    );
}

// Styles
const createPageStyles = (theme: ThemeType) => StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    header: {
        padding: theme.spacing.lg,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
    },
    title: {
        fontSize: theme.fontSize.xxl,
        fontWeight: 'bold',
        color: theme.colors.text,
        marginBottom: theme.spacing.xs,
    },
    subtitle: {
        fontSize: theme.fontSize.sm,
        color: theme.colors.textSecondary,
    },
    actions: {
        flexDirection: 'row',
        padding: theme.spacing.lg,
        gap: theme.spacing.md,
    },
    refreshButton: {
        flex: 1,
        backgroundColor: theme.colors.primary,
        padding: theme.spacing.md,
        borderRadius: theme.borderRadius.md,
        alignItems: 'center',
    },
    refreshButtonText: {
        color: '#ffffff',
        fontSize: theme.fontSize.sm,
        fontWeight: '600',
    },
    clearAllButton: {
        flex: 1,
        backgroundColor: theme.colors.error,
        padding: theme.spacing.md,
        borderRadius: theme.borderRadius.md,
        alignItems: 'center',
    },
    clearAllButtonText: {
        color: '#ffffff',
        fontSize: theme.fontSize.sm,
        fontWeight: '600',
    },
    section: {
        padding: theme.spacing.lg,
    },
    sectionTitle: {
        fontSize: theme.fontSize.lg,
        fontWeight: 'bold',
        color: theme.colors.text,
        marginBottom: theme.spacing.md,
    },
    emptyText: {
        fontSize: theme.fontSize.sm,
        color: theme.colors.textSecondary,
        fontStyle: 'italic',
        textAlign: 'center',
        padding: theme.spacing.lg,
    },
    bottomSpacing: {
        height: theme.spacing.xl,
    },
});

const createStatsStyles = (theme: ThemeType) => StyleSheet.create({
    container: {
        margin: theme.spacing.lg,
        padding: theme.spacing.lg,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.md,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    title: {
        fontSize: theme.fontSize.lg,
        fontWeight: 'bold',
        color: theme.colors.text,
        marginBottom: theme.spacing.md,
        textAlign: 'center',
    },
    statsRow: {
        flexDirection: 'row',
        justifyContent: 'space-around',
    },
    statItem: {
        alignItems: 'center',
    },
    statNumber: {
        fontSize: theme.fontSize.lg,
        fontWeight: 'bold',
        color: theme.colors.primary,
    },
    statLabel: {
        fontSize: theme.fontSize.xs,
        color: theme.colors.textSecondary,
        marginTop: theme.spacing.xs,
    },
});

const createDataItemStyles = (theme: ThemeType) => StyleSheet.create({
    container: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.md,
        borderWidth: 1,
        borderColor: theme.colors.border,
        marginBottom: theme.spacing.md,
        overflow: 'hidden',
    },
    header: {
        padding: theme.spacing.md,
    },
    titleRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing.xs,
    },
    title: {
        fontSize: theme.fontSize.md,
        fontWeight: '600',
        color: theme.colors.text,
        flex: 1,
    },
    size: {
        fontSize: theme.fontSize.xs,
        color: theme.colors.primary,
        fontWeight: '600',
    },
    metaRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    type: {
        fontSize: theme.fontSize.xs,
        color: theme.colors.textSecondary,
    },
    expandIcon: {
        fontSize: theme.fontSize.sm,
        color: theme.colors.textSecondary,
    },
    content: {
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
    },
    dataContainer: {
        maxHeight: 300,
        padding: theme.spacing.md,
        backgroundColor: theme.colors.background,
    },
    dataText: {
        fontSize: theme.fontSize.xs,
        color: theme.colors.text,
        fontFamily: 'monospace',
        lineHeight: 16,
    },
    clearButton: {
        backgroundColor: theme.colors.error,
        padding: theme.spacing.sm,
        alignItems: 'center',
    },
    clearButtonText: {
        color: '#ffffff',
        fontSize: theme.fontSize.xs,
        fontWeight: '600',
    },
});
